import { employeeWorkflowApi } from "@/api/employees/employee-workflow.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiEmployeeWorkflowRunPayload,
  ApiWorkflowFilter,
} from "@/types/workflows/workflow.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const employeeWorkflowKeys = createQueryKeys("employeeWorkflows", {
  search: (employeeId: number, filter: ApiWorkflowFilter) => ({
    queryKey: [employeeId, filter],
  }),
});

export const useEmployeeWorkflowsSearch = (
  employeeId: number | null,
  filter: ApiWorkflowFilter,
  enabled = true
) => {
  return useQuery({
    ...employeeWorkflowKeys.search(employeeId ?? 0, filter),
    enabled: enabled && employeeId !== null,
    queryFn: async ({ signal }) => {
      if (!employeeId) return null;
      const result = await employeeWorkflowApi.search(employeeId, filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useEmployeeWorkflowRun = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      employeeId,
      workflowId,
      payload,
      signal,
    }: {
      employeeId: number;
      workflowId: number;
      payload?: ApiEmployeeWorkflowRunPayload;
      signal?: AbortSignal;
    }) => {
      const result = await employeeWorkflowApi.run(employeeId, workflowId, payload, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: employeeWorkflowKeys._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
